import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { FileCog, Download, Upload, Trash2, Loader2, FolderOpen, Info, AlertTriangle, Edit3, Save, X, ShieldCheck, Eye, Copy } from 'lucide-react';

/*
 * v1.2.0 — Template Editor (Amministrazione)
 * Permette agli admin di vedere, scaricare e sostituire TUTTI i template
 * del sistema senza dover passare dal sistemista.
 */

export default function TemplateEditorPage({ onlyCategories = null, pageTitle = null, pageSubtitle = null }) {
  // v1.2.6: Supporto filtro categorie tramite prop o query param ?only=registri,attestati
  // Usato per riusare lo stesso editor sotto Formazione (solo registri/attestati)
  // o sotto altre sezioni con sottoinsiemi di template.
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const onlyFromUrl = urlParams.get('only');
  const restrictedCategories = onlyCategories || (onlyFromUrl ? onlyFromUrl.split(',').map(s => s.trim()).filter(Boolean) : null);

  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [files, setFiles] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  // Editor inline
  const [editingFile, setEditingFile] = useState(null);
  const [editorItems, setEditorItems] = useState([]);
  const [editorPlaceholders, setEditorPlaceholders] = useState([]);
  const [editorReadonly, setEditorReadonly] = useState(false);
  const [editorReason, setEditorReason] = useState('');
  const [savingEditor, setSavingEditor] = useState(false);
  const [editorFilter, setEditorFilter] = useState('');
  const [lastFocusedIdx, setLastFocusedIdx] = useState(null);
  // Preview HTML (mammoth)
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewPlaceholders, setPreviewPlaceholders] = useState([]);
  const [previewSuggested, setPreviewSuggested] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');

  // v1.2.7: Dialog Anteprima standalone (separato da Modifica)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFilename, setPreviewFilename] = useState('');
  const [standalonePreviewHtml, setStandalonePreviewHtml] = useState('');
  const [standalonePreviewPh, setStandalonePreviewPh] = useState([]);
  const [standalonePreviewSug, setStandalonePreviewSug] = useState([]);
  const [standalonePreviewLoading, setStandalonePreviewLoading] = useState(false);

  const loadCats = async () => {
    try {
      const r = await api.get('/template-editor/categories');
      let cats = r.data || [];
      // v1.2.6: filtro categorie se restrictedCategories impostato
      if (restrictedCategories && restrictedCategories.length > 0) {
        cats = cats.filter(c => restrictedCategories.includes(c.id));
      }
      setCategories(cats);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Accesso negato — solo admin');
    } finally {
      setLoading(false);
    }
  };

  const openCategory = async (cat) => {
    setActiveCategory(cat);
    try {
      const [fRes, pRes] = await Promise.all([
        api.get(`/template-editor/list/${cat.id}`),
        api.get(`/template-editor/placeholders/${cat.id}`).catch(() => ({ data: { placeholders: [] } })),
      ]);
      setFiles(fRes.data || []);
      setPlaceholders(pRes.data?.placeholders || []);
    } catch (e) {
      toast.error('Errore caricamento file');
    }
  };

  const handleDownload = async (filename) => {
    try {
      const r = await api.get(`/template-editor/download/${activeCategory.id}/${filename}`, { responseType: 'blob' });
      const blob = new Blob([r.data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error('Download fallito');
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/template-editor/upload/${activeCategory.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`File "${file.name}" caricato. Backup automatico creato.`);
      openCategory(activeCategory);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fallito');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Spostare in backup "${filename}"? (Il file verrà rinominato in _backup_TS_${filename}, ma resterà sul disco.)`)) return;
    try {
      await api.delete(`/template-editor/${activeCategory.id}/${filename}`);
      toast.success('File spostato in backup');
      openCategory(activeCategory);
    } catch (e) {
      toast.error('Eliminazione fallita');
    }
  };

  // v1.3.22: elimina definitivamente i backup più vecchi (mantiene gli ultimi 2 per file)
  const cleanupBackups = async () => {
    if (!activeCategory) return;
    if (!window.confirm('Eliminare DEFINITIVAMENTE i backup più vecchi? Verranno mantenuti solo gli ultimi 2 per ogni template.')) return;
    try {
      const r = await api.post(`/template-editor/cleanup-backups/${activeCategory.id}?keep=2`);
      toast.success(`${r.data.deleted} backup eliminati (mantenuti ${r.data.kept}).`);
      openCategory(activeCategory);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pulizia backup fallita');
    }
  };

  // ----- INLINE EDITOR -----
  const openInlineEditor = async (filename) => {
    setEditingFile(filename);
    setEditorItems([]);
    setEditorPlaceholders([]);
    setEditorReadonly(false);
    setEditorReason('');
    setEditorFilter('');
    setActiveTab('preview');
    setPreviewHtml('');
    setPreviewPlaceholders([]);
    setPreviewSuggested([]);
    setPreviewLoading(true);
    try {
      // 1) Carico contenuto strutturato (paragrafi) per editing
      const r = await api.get(`/template-editor/content/${activeCategory.id}/${filename}`);
      if (r.data.readonly) {
        setEditorReadonly(true);
        setEditorReason(r.data.reason || '');
      } else {
        setEditorItems((r.data.items || []).map((it) => ({ ...it, new_text: it.text })));
        setEditorPlaceholders(r.data.placeholders_found || []);
      }
      // 2) Carico in parallelo l'anteprima HTML formattata (mammoth)
      try {
        const pr = await api.get(`/template-editor/preview-html/${activeCategory.id}/${filename}`);
        setPreviewHtml(pr.data.html || '');
        setPreviewPlaceholders(pr.data.placeholders || []);
        setPreviewSuggested(pr.data.suggested || []);
      } catch (pe) {
        setPreviewHtml(`<div class="ph-notice">Anteprima non disponibile: ${pe.response?.data?.detail || pe.message}</div>`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento contenuto');
      setEditingFile(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const copyPlaceholder = async (raw) => {
    try {
      await navigator.clipboard.writeText(raw);
      toast.success(`Copiato: ${raw}`);
    } catch {
      toast.error('Copia fallita — copia manualmente');
    }
  };

  // v1.2.7: Apertura del dialog Anteprima standalone (solo lettura, con sidebar copia placeholder)
  const openStandalonePreview = async (filename) => {
    setPreviewOpen(true);
    setPreviewFilename(filename);
    setStandalonePreviewHtml('');
    setStandalonePreviewPh([]);
    setStandalonePreviewSug([]);
    setStandalonePreviewLoading(true);
    try {
      const pr = await api.get(`/template-editor/preview-html/${activeCategory.id}/${filename}`);
      setStandalonePreviewHtml(pr.data.html || '');
      setStandalonePreviewPh(pr.data.placeholders || []);
      setStandalonePreviewSug(pr.data.suggested || []);
    } catch (pe) {
      setStandalonePreviewHtml(`<div class="ph-notice">Anteprima non disponibile: ${pe.response?.data?.detail || pe.message}</div>`);
    } finally {
      setStandalonePreviewLoading(false);
    }
  };

  const saveInlineEditor = async () => {
    setSavingEditor(true);
    try {
      const payload = {
        items: editorItems
          .filter((it) => it.new_text !== it.text)
          .map((it) => ({ id: it.id, new_text: it.new_text })),
      };
      if (payload.items.length === 0) {
        toast.info('Nessuna modifica da salvare');
        setSavingEditor(false);
        return;
      }
      const r = await api.post(`/template-editor/content/${activeCategory.id}/${editingFile}`, payload);
      toast.success(`${r.data.edits_applied} modifiche salvate. Backup: ${r.data.backup}`);
      setEditingFile(null);
      openCategory(activeCategory);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio fallito');
    } finally {
      setSavingEditor(false);
    }
  };

  const validateFile = async (filename) => {
    try {
      const r = await api.post(`/template-editor/validate/${activeCategory.id}/${filename}`);
      const v = r.data;
      if (v.ok && (v.warnings || []).length === 0) {
        toast.success(`${filename} — OK. Placeholder trovati: ${v.placeholders?.length || 0}`);
      } else if (v.ok) {
        toast.warning(`${filename} OK ma con avvisi: ${(v.warnings || []).join(' · ')}`);
      } else {
        toast.error(`${filename} ROTTO: ${(v.errors || []).join(' · ')}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Validazione fallita');
    }
  };

  const insertPlaceholder = (idx, ph) => {
    setEditorItems((items) => {
      const copy = [...items];
      copy[idx] = { ...copy[idx], new_text: (copy[idx].new_text || '') + ph };
      return copy;
    });
  };

  useEffect(() => { loadCats(); }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="template-editor-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow">
          <FileCog className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{pageTitle || 'Template Editor (Admin)'}</h1>
          <p className="text-sm text-slate-500">{pageSubtitle || 'Scarica, modifica e ricarica i template del sistema. Backup automatico ad ogni upload.'}</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
        <div className="text-xs text-amber-900">
          <strong>Come modificare un template:</strong> 1) Scarica il file. 2) Aprilo con Word/LibreOffice e modifica i placeholder (es. <code>{'{{ nome }}'}</code>, <code>{'{{ data_corso }}'}</code>).
          3) Salva con LO STESSO nome. 4) Ricaricalo qui — il backup automatico viene fatto. Per vedere i placeholder disponibili clicca &quot;Vedi placeholder&quot;.
        </div>
      </div>

      {!activeCategory ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="category-list">
          {categories.map((c) => (
            <Card
              key={c.id}
              className={`border ${c.readonly_info ? 'border-slate-200 bg-slate-50' : 'border-slate-200 hover:shadow-md hover:border-slate-400 cursor-pointer'} transition`}
              onClick={() => !c.readonly_info && openCategory(c)}
              data-testid={`cat-${c.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-slate-700" />
                    <h3 className="font-semibold text-slate-900">{c.label}</h3>
                  </div>
                  {!c.readonly_info && <Badge variant="outline">{c.n_files} file</Badge>}
                  {c.readonly_info && <Badge variant="secondary">read-only</Badge>}
                </div>
                <p className="text-xs text-slate-500">{c.description}</p>
                {!c.readonly_info && (
                  <p className="text-[11px] text-slate-400 mt-1">Estensioni: {c.exts.join(', ')}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setActiveCategory(null); setFiles([]); }}>
                ← Categorie
              </Button>
              <h2 className="text-lg font-semibold text-slate-800">{activeCategory.label}</h2>
              <Badge variant="outline">{files.length} file</Badge>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={cleanupBackups} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="cleanup-backups-btn">
                <Trash2 className="w-3 h-3 mr-1" /> Elimina backup vecchi
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowPlaceholders(true)} data-testid="show-placeholders-btn">
                <Info className="w-3 h-3 mr-1" /> Vedi placeholder
              </Button>
              <label className="cursor-pointer">
                <input type="file" accept={activeCategory.exts?.join(',')} className="hidden" onChange={handleUpload} disabled={uploading} data-testid="upload-input" />
                <span className="inline-flex items-center px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 cursor-pointer">
                  {uploading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Upload...</> : <><Upload className="w-3 h-3 mr-1" /> Carica nuovo</>}
                </span>
              </label>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100" data-testid="file-list">
            {files.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">Nessun file in questa categoria</div>
            ) : (
              files.map((f) => (
                <div key={f.filename} className={`px-4 py-3 flex items-center justify-between gap-3 ${f.filename.startsWith('_backup_') ? 'opacity-60' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-slate-800 truncate" title={f.filename}>{f.filename}</div>
                    <div className="text-xs text-slate-500">{f.size_kb} KB · modificato {f.modified_at?.slice(0, 16).replace('T', ' ')}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => handleDownload(f.filename)} data-testid={`dl-${f.filename}`}>
                      <Download className="w-3 h-3 mr-1" /> Scarica
                    </Button>
                    {!f.filename.startsWith('_backup_') && (f.ext === '.docx' || f.ext === '.dotx') && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => openStandalonePreview(f.filename)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`preview-${f.filename}`}>
                          <Eye className="w-3 h-3 mr-1" /> Anteprima
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => validateFile(f.filename)} title="Verifica integrità Jinja/docxtpl" data-testid={`validate-${f.filename}`}>
                          <ShieldCheck className="w-3 h-3 mr-1" /> Verifica
                        </Button>
                        <Button size="sm" onClick={() => openInlineEditor(f.filename)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`edit-${f.filename}`}>
                          <Edit3 className="w-3 h-3 mr-1" /> Modifica
                        </Button>
                      </>
                    )}
                    {!f.filename.startsWith('_backup_') && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(f.filename)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <Dialog open={showPlaceholders} onOpenChange={setShowPlaceholders}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Placeholder disponibili — {activeCategory?.label}</DialogTitle>
            <DialogDescription>
              Usa questi placeholder nel template Word/LibreOffice. Il sistema li sostituirà automaticamente con i dati reali.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-1 text-sm">
            {placeholders.map((p, i) => {
              const m = String(p).match(/\{\{[^}]*\}\}|\{%[^%]*%\}|«[^»]+»/);
              if (!m) {
                // riga divisore / etichetta di sezione (non copiabile)
                return (
                  <li key={i} className="pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {String(p).replace(/^[—\s-]+|[—\s-]+$/g, '')}
                  </li>
                );
              }
              const token = m[0];
              const desc = String(p).slice(m.index + m[0].length).replace(/^\s*[—-]\s*/, '');
              return (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => copyPlaceholder(token)}
                    title="Clicca per copiare"
                    data-testid={`copy-ph-${i}`}
                    className="w-full text-left flex items-center gap-2 bg-slate-50 hover:bg-amber-50 border border-transparent hover:border-amber-300 rounded px-2 py-1.5 transition-colors group"
                  >
                    <Copy className="w-3 h-3 text-slate-400 group-hover:text-amber-600 flex-shrink-0" />
                    <span className="font-mono text-xs text-slate-800 whitespace-nowrap">{token}</span>
                    {desc && <span className="text-xs text-slate-500 truncate">— {desc}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-slate-500 mt-3">
            💡 Clicca un placeholder per copiarlo, poi incollalo nel template Word/LibreOffice. Funziona sia in stile <code>{'{{ Nome }}'}</code> sia <code>«Nome»</code>.
          </p>
        </DialogContent>
      </Dialog>

      {/* INLINE EDITOR DIALOG */}
      <Dialog open={!!editingFile} onOpenChange={(o) => !o && setEditingFile(null)}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="inline-editor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5" /> Modifica inline: <span className="font-mono text-sm">{editingFile}</span>
            </DialogTitle>
            <DialogDescription>
              Modifica il testo di ogni paragrafo/cella. Aggiungi placeholder con i bottoni a destra. Backup automatico al salvataggio.
            </DialogDescription>
          </DialogHeader>

          {editorReadonly ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 inline mr-2" />
              <span className="text-sm text-amber-900">{editorReason}</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
              {/* Tabs: Anteprima formattata | Modifica testi */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid grid-cols-2 mb-2">
                  <TabsTrigger value="preview" data-testid="tab-preview">
                    <Eye className="w-3 h-3 mr-1" /> Anteprima formattata
                  </TabsTrigger>
                  <TabsTrigger value="edit" data-testid="tab-edit">
                    <Edit3 className="w-3 h-3 mr-1" /> Modifica testi ({editorItems.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="preview">
                  <div className="bg-white border border-slate-200 rounded-xl shadow-inner overflow-y-auto max-h-[65vh] p-6 docx-preview" data-testid="preview-html">
                    {previewLoading ? (
                      <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: previewHtml || '<div class="text-slate-400 italic">Anteprima vuota</div>' }} />
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    I <mark className="ph">{'{{ placeholder }}'}</mark> evidenziati in giallo sono i campi che verranno sostituiti con i dati reali.
                    Passa al tab &quot;Modifica testi&quot; per cambiare il contenuto.
                  </p>
                </TabsContent>

                <TabsContent value="edit">
                  <div className="flex items-center gap-2 mb-2">
                    <Input
                      placeholder="Cerca testo nel documento..."
                      value={editorFilter}
                      onChange={(e) => setEditorFilter(e.target.value)}
                      className="text-sm"
                      data-testid="editor-filter"
                    />
                    <Badge variant="outline">{editorItems.length} elementi</Badge>
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-2">
                    {editorItems.map((it, idx) => {
                      const safeText = (it.text || '').toLowerCase();
                      const safeNew = (it.new_text || '').toLowerCase();
                      if (editorFilter && !safeText.includes(editorFilter.toLowerCase()) && !safeNew.includes(editorFilter.toLowerCase())) {
                        return null;
                      }
                      const changed = it.new_text !== it.text;
                      return (
                        <div key={it.id} className={`rounded-lg border p-2 ${changed ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] font-mono text-slate-500">{it.id} ({it.type})</span>
                            {changed && <Badge className="bg-blue-600 text-white text-[10px]">modificato</Badge>}
                          </div>
                          <Textarea
                            rows={Math.max(1, Math.ceil((it.new_text || '').length / 80))}
                            value={it.new_text || ''}
                            onFocus={() => setLastFocusedIdx(idx)}
                            onChange={(e) => {
                              const v = e.target.value;
                              setEditorItems((items) => items.map((x, i) => i === idx ? { ...x, new_text: v } : x));
                            }}
                            className="text-xs font-mono"
                            data-testid={`editor-item-${it.id}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>
              </Tabs>

              {/* Sidebar placeholder USER-FRIENDLY (click to copy) */}
              <div className="bg-slate-50 rounded-xl p-3 space-y-3 max-h-[70vh] overflow-y-auto">
                {previewPlaceholders.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-emerald-800 mb-1 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> Già usati ({previewPlaceholders.length})
                    </h3>
                    <div className="space-y-1">
                      {previewPlaceholders.map((p, i) => (
                        <button
                          key={`used-${i}`}
                          type="button"
                          onClick={() => copyPlaceholder(p.raw)}
                          className="block w-full text-left rounded px-2 py-1 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition"
                          data-testid={`ph-used-${i}`}
                          title={`Clicca per copiare: ${p.raw}`}
                        >
                          <div className="text-[11px] font-semibold text-emerald-900">{p.label}</div>
                          <div className="font-mono text-[10px] text-emerald-700 truncate">{p.raw}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {previewSuggested.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-800 mb-1 flex items-center gap-1">
                      <Info className="w-3 h-3" /> Suggeriti per {activeCategory?.label}
                    </h3>
                    <div className="space-y-1">
                      {previewSuggested.map((p, i) => {
                        const alreadyUsed = previewPlaceholders.some((u) => u.raw === p.raw);
                        return (
                          <button
                            key={`sug-${i}`}
                            type="button"
                            onClick={() => copyPlaceholder(p.raw)}
                            disabled={alreadyUsed}
                            className={`block w-full text-left rounded px-2 py-1 border transition ${alreadyUsed ? 'bg-slate-100 border-slate-200 opacity-50' : 'bg-white border-slate-200 hover:bg-violet-50 hover:border-violet-300'}`}
                            data-testid={`ph-sug-${i}`}
                            title={`Clicca per copiare: ${p.raw}`}
                          >
                            <div className="flex items-center justify-between gap-1">
                              <div className="text-[11px] font-semibold text-slate-800 truncate">{p.label}</div>
                              {alreadyUsed ? (
                                <span className="text-[9px] text-emerald-600">✓ usato</span>
                              ) : (
                                <Copy className="w-3 h-3 text-slate-400 flex-shrink-0" />
                              )}
                            </div>
                            <div className="font-mono text-[10px] text-slate-500 truncate">{p.raw}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="text-[10px] text-slate-500 italic border-t pt-2">
                  💡 Clicca su un placeholder per copiarlo. Poi vai sul tab &quot;Modifica testi&quot; e incollalo dove serve.
                </div>
                {/* v1.2.8: Info firma docente auto */}
                {(activeCategory?.id === 'registri' || activeCategory?.id === 'attestati') && (
                  <div className="text-[10px] bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900 leading-relaxed mt-1">
                    <strong>🖋️ Firma docente automatica:</strong> i placeholder <code className="font-mono text-[10px] bg-white px-1 rounded">{'{{ Docente_Firma }}'}</code> e <code className="font-mono text-[10px] bg-white px-1 rounded">{'{{ Docente_Timbro }}'}</code> vengono sostituiti automaticamente con la firma/timbro caricati nella scheda del docente (Gestione Docenti → Firma autografa). Non devi inserire nulla a mano.
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setEditingFile(null)} disabled={savingEditor}>
              <X className="w-3 h-3 mr-1" /> Annulla
            </Button>
            {!editorReadonly && (
              <Button onClick={saveInlineEditor} disabled={savingEditor} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-editor-btn">
                {savingEditor ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Salvataggio...</> : <><Save className="w-4 h-4 mr-1" /> Salva modifiche</>}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* v1.2.7: Dialog ANTEPRIMA standalone (solo lettura, sidebar placeholder click-to-copy) */}
      <Dialog open={previewOpen} onOpenChange={(o) => { if (!o) { setPreviewOpen(false); setStandalonePreviewHtml(''); } }}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-emerald-600" />
              Anteprima — {previewFilename}
            </DialogTitle>
            <DialogDescription>
              Visualizzazione formattata del template. Clicca un placeholder a destra per copiarlo negli appunti.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
            {/* Anteprima HTML */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-inner overflow-y-auto max-h-[65vh] p-6 docx-preview" data-testid="standalone-preview-html">
              {standalonePreviewLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: standalonePreviewHtml || '<div class="text-slate-400 italic">Anteprima vuota</div>' }} />
              )}
            </div>

            {/* Sidebar placeholder click-to-copy */}
            <div className="bg-slate-50 rounded-xl p-3 space-y-3 max-h-[65vh] overflow-y-auto">
              {standalonePreviewPh.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-emerald-800 mb-1 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Placeholder nel doc ({standalonePreviewPh.length})
                  </h3>
                  <div className="space-y-1">
                    {standalonePreviewPh.map((p, i) => (
                      <button
                        key={`sp-used-${i}`}
                        type="button"
                        onClick={() => copyPlaceholder(p.raw)}
                        className="block w-full text-left rounded px-2 py-1 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition"
                        data-testid={`sp-ph-used-${i}`}
                        title={`Clicca per copiare: ${p.raw}`}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <div className="text-[11px] font-semibold text-emerald-900 truncate">{p.label}</div>
                          <Copy className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                        </div>
                        <div className="font-mono text-[10px] text-emerald-700 truncate">{p.raw}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {standalonePreviewSug.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-slate-800 mb-1 flex items-center gap-1">
                    <Info className="w-3 h-3" /> Suggeriti per {activeCategory?.label}
                  </h3>
                  <div className="space-y-1">
                    {standalonePreviewSug.map((p, i) => {
                      const alreadyUsed = standalonePreviewPh.some((u) => u.raw === p.raw);
                      return (
                        <button
                          key={`sp-sug-${i}`}
                          type="button"
                          onClick={() => copyPlaceholder(p.raw)}
                          disabled={alreadyUsed}
                          className={`block w-full text-left rounded px-2 py-1 border transition ${alreadyUsed ? 'bg-slate-100 border-slate-200 opacity-50' : 'bg-white border-slate-200 hover:bg-violet-50 hover:border-violet-300'}`}
                          data-testid={`sp-ph-sug-${i}`}
                          title={`Clicca per copiare: ${p.raw}`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <div className="text-[11px] font-semibold text-slate-800 truncate">{p.label}</div>
                            {alreadyUsed ? (
                              <span className="text-[9px] text-emerald-600">✓ usato</span>
                            ) : (
                              <Copy className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            )}
                          </div>
                          <div className="font-mono text-[10px] text-slate-500 truncate">{p.raw}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="text-[10px] text-slate-500 italic border-t pt-2">
                💡 Clicca un placeholder per copiarlo. Poi vai sul pulsante &quot;Modifica&quot; per incollarlo dove serve nel testo.
              </div>
              {/* v1.2.8: Info firma docente auto */}
              {(activeCategory?.id === 'registri' || activeCategory?.id === 'attestati') && (
                <div className="text-[10px] bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900 leading-relaxed mt-1">
                  <strong>🖋️ Firma docente automatica:</strong> i placeholder <code className="font-mono text-[10px] bg-white px-1 rounded">{'{{ Docente_Firma }}'}</code> e <code className="font-mono text-[10px] bg-white px-1 rounded">{'{{ Docente_Timbro }}'}</code> vengono sostituiti automaticamente con la firma/timbro caricati nella scheda del docente (Gestione Docenti → Firma autografa).
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between gap-2 mt-3">
            <Button variant="outline" onClick={() => handleDownload(previewFilename)} data-testid="preview-download-btn">
              <Download className="w-4 h-4 mr-1" /> Scarica
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setPreviewOpen(false)} data-testid="preview-close-btn">
                <X className="w-4 h-4 mr-1" /> Chiudi
              </Button>
              <Button onClick={() => { setPreviewOpen(false); openInlineEditor(previewFilename); }} className="bg-blue-600 hover:bg-blue-700" data-testid="preview-edit-btn">
                <Edit3 className="w-4 h-4 mr-1" /> Apri in Modifica
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
